export function formatAiPrompt(text: string, joinWrappedLines: boolean): string {
    const lines = text.replace(/\r\n?/g, "\n").split("\n");

    while (lines.length > 0 && lines[0].trim() === "") {
        lines.shift();
    }

    while (lines.length > 0 && lines[lines.length - 1].trim() === "") {
        lines.pop();
    }

    const nonEmptyLines = lines.filter(line => line.trim() !== "");
    let indent = commonIndent(nonEmptyLines);

    // Pasting after the cursor can leave only the first line without the copied indentation.
    if (indent === "" && nonEmptyLines.length > 1 && isProseLine(nonEmptyLines[0])) {
        const remainingIndent = commonIndent(nonEmptyLines.slice(1));

        // Four spaces or a tab can introduce an intentional Markdown code block.
        if (/^ {1,3}$/.test(remainingIndent)) {
            indent = remainingIndent;
        }
    }

    const unindentedLines = lines.map(line => line.startsWith(indent) ? line.slice(indent.length) : line);

    return (joinWrappedLines ? joinParagraphLines(unindentedLines) : unindentedLines).join("\n");
}

function commonIndent(lines: string[]): string {
    if (lines.length === 0) {
        return "";
    }

    let indent = lines[0].match(/^[ \t]*/)[0];

    for (const line of lines.slice(1)) {
        while (!line.startsWith(indent)) {
            indent = indent.slice(0, -1);
        }
    }

    return indent;
}

function joinParagraphLines(lines: string[]): string[] {
    const result: string[] = [];
    let paragraph: string[] = [];
    let fence: {marker: string; length: number} | undefined;

    for (const line of lines) {
        const fenceMatch = line.match(/^ {0,3}(`{3,}|~{3,})(.*)$/);

        if (fence) {
            result.push(line);

            if (fenceMatch && fenceMatch[1][0] === fence.marker && fenceMatch[1].length >= fence.length && fenceMatch[2].trim() === "") {
                fence = undefined;
            }
        } else if (fenceMatch) {
            flushParagraph();
            result.push(line);

            fence = {marker: fenceMatch[1][0], length: fenceMatch[1].length};
        } else if (line.trim() === "") {
            flushParagraph();
            result.push(line);
        } else {
            paragraph.push(line);
        }
    }

    flushParagraph();

    return result;

    function flushParagraph() {
        if (paragraph.length > 1 && paragraph.every(isProseLine)) {
            result.push(paragraph.map((line, index) => index < paragraph.length - 1 ? line.trimEnd() : line).join(" "));
        } else {
            result.push(...paragraph);
        }

        paragraph = [];
    }
}

function isProseLine(line: string): boolean {
    // Preserve indentation, explicit Markdown line breaks, and block-level syntax.
    if (/^[ \t]| {2,}$|\\$/.test(line)) {
        return false;
    }

    if (/^(?:#{1,6}(?:\s|$)|[-+*](?:\s|$)|\d+[.)]\s|>|<|[{}\[\]]|(?:[-*_]\s*){3,}$|=+\s*$)/.test(line)) {
        return false;
    }

    // Labels and examples are often intentionally separated without an empty line.
    return !line.includes("|")
        && !line.includes("`")
        && !/:\s*$/.test(line)
        && !/^[\p{L}\p{N} _-]{1,40}:\s/u.test(line);
}
